export type SpTargetRow = {
  target_id?: string;
  campaign_id?: string;
  ad_group_id?: string;
  state?: string;
  bid?: number;
  expression_type?: string;
  expression?: Array<Record<string, unknown>>;
  resolved_expression?: Array<Record<string, unknown>>;
};

export function parseSpTargetsPayload(data: unknown): {
  targets: SpTargetRow[];
  totalResults: number;
  nextToken: string | null;
} {
  if (!data || typeof data !== 'object') {
    return { targets: [], totalResults: 0, nextToken: null };
  }
  const raw = data as Record<string, unknown>;
  const targets = Array.isArray(raw.targets) ? (raw.targets as SpTargetRow[]) : [];
  const totalResults = typeof raw.total_results === 'number' ? raw.total_results : targets.length;
  const nextToken =
    typeof raw.next_token === 'string' && raw.next_token.trim() ? raw.next_token : null;
  return { targets, totalResults, nextToken };
}

function expressionLabels(expression: unknown[]): string[] {
  return expression
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const item = entry as Record<string, unknown>;
      const type = typeof item.type === 'string' ? item.type : null;
      // SD audience clauses nest their refinements, e.g. views → [category, lookback].
      if (Array.isArray(item.value)) {
        const nested = expressionLabels(item.value);
        if (nested.length === 0) return type;
        return type ? `${type} (${nested.join(', ')})` : nested.join(', ');
      }
      const value = typeof item.value === 'string' ? item.value : null;
      if (type && value) return `${type}: ${value}`;
      return type || value;
    })
    .filter((value): value is string => Boolean(value));
}

export function formatTargetExpression(expression: unknown): string {
  if (!Array.isArray(expression) || expression.length === 0) return '-';
  const labels = expressionLabels(expression);
  return labels.length > 0 ? labels.join(', ') : '-';
}

const THEME_OR_AUTO_EXPRESSION_TYPES = new Set([
  'CLOSE_MATCH',
  'LOOSE_MATCH',
  'SUBSTITUTES',
  'COMPLEMENTS',
  'QUERY_HIGH_REL_MATCHES',
  'QUERY_BROAD_REL_MATCHES',
  'ASIN_SUBSTITUTE_RELATED',
  'ASIN_ACCESSORY_RELATED',
  'KEYWORDS_CLOSE_MATCH',
  'KEYWORDS_LOOSE_MATCH',
  'PRODUCT_SUBSTITUTES',
  'PRODUCT_COMPLEMENTS',
  'SEARCH_CLOSE_MATCH',
  'SEARCH_LOOSE_MATCH',
  'KEYWORDS_RELATED_TO_YOUR_BRAND',
  'KEYWORDS_RELATED_TO_GIFTS',
  'KEYWORDS_RELATED_TO_YOUR_PRODUCT_CATEGORY',
  'KEYWORDS_RELATED_TO_YOUR_PRODUCTS',
  'KEYWORDS_RELATED_TO_PEER_BRANDS_PRODUCT_CATEGORY',
  'KEYWORDS_RELATED_TO_PRIME_DAY',
  'BRAND',
  'GIFT',
  'CATEGORY',
  'THEME',
]);

export function isProductTargetingClause(target: SpTargetRow): boolean {
  const expressionType = (target.expression_type || '').trim().toUpperCase();
  if (expressionType === 'AUTO') return false;
  const types = (target.expression || [])
    .map((entry) => (typeof entry?.type === 'string' ? entry.type.trim().toUpperCase() : ''))
    .filter(Boolean);
  if (types.some((type) => THEME_OR_AUTO_EXPRESSION_TYPES.has(type))) {
    return false;
  }
  return true;
}

export function targetExpressionsDiffer(
  expression: unknown,
  resolvedExpression: unknown
): boolean {
  return formatTargetExpression(expression) !== formatTargetExpression(resolvedExpression);
}
